/**
 * OCR Lab — run one page through both models and compare.
 *
 * Layout is deliberately three bands rather than a dense dashboard:
 *   1. Model bar   — start/stop + honest phase, always visible
 *   2. Source      — the page image, with optional bbox overlay per model
 *   3. Results     — two columns, same scroll, diff-friendly
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ComparisonBar } from "@/components/ComparisonBar";
import { ModelBar } from "@/components/ModelBar";
import { PageCanvas } from "@/components/PageCanvas";
import { ResultColumn } from "@/components/ResultColumn";
import { renderPdfPage, type RenderedPage } from "@/lib/pdf";
import { api } from "@/lib/api";
import { Button } from "@/components/ui";
import type { ModelInfo, RunResult, Status } from "@/lib/types";

export default function OcrLab() {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [status, setStatus] = useState<Record<string, Status>>({});
  const [prompts, setPrompts] = useState<Record<string, string>>({});
  const [page, setPage] = useState<RenderedPage | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [pdf, setPdf] = useState<ArrayBuffer | null>(null);
  const [uploadKey, setUploadKey] = useState("");
  const [results, setResults] = useState<RunResult[] | null>(null);
  const [running, setRunning] = useState(false);
  const [overlay, setOverlay] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [err, setErr] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  // Minutes of inactivity before the cluster-side reaper stops a model. Exposed
  // because the right value depends on what you are doing: a quick check wants
  // it short so the GPU stops paying, a long session wants it long so a model
  // is not pulled out from under you mid-comparison.
  const [idleTimeout, setIdleTimeout] = useState(20);
  // How many pages to send, starting at the current one. More than one is the
  // only way to exercise Unlimited-OCR's single-pass multi-page claim — at one
  // page the two models look architecturally identical.
  const [pageCountToRun, setPageCountToRun] = useState(1);

  // Reopening a saved run: /ocrlab?run=<id> loads its results and its page, so
  // a comparison can be revisited rather than only remembered.
  useEffect(() => {
    const runId = new URLSearchParams(window.location.search).get("run");
    if (!runId) return;
    (async () => {
      try {
        const run = await api.get(`/api/ocrlab/runs/${runId}`);
        setResults(run.results ?? null);
        setPrompts((p) => ({ ...p, ...(run.prompts ?? {}) }));
        setPageNum(run.pageNumber ?? 1);
        if (run.pageKey) {
          const { imageBase64 } = await api.get(`/api/ocrlab/page?key=${encodeURIComponent(run.pageKey)}`);
          setPage({ base64: imageBase64, width: run.page?.width ?? 0, height: run.page?.height ?? 0 });
        }
        (window as any).__lastRun = run;
        setSaved(true);
      } catch { setErr("Could not load that saved run."); }
    })();
  }, []);

  useEffect(() => {
    api.get("/api/ocrlab/models").then((d) => {
      setModels(d.models);
      setSelected(d.models.map((m: ModelInfo) => m.id));
      setPrompts(Object.fromEntries(d.models.map((m: ModelInfo) => [m.id, m.defaultPrompt])));
    });
  }, []);

  // Poll only while something is mid-transition. A GPU bench that polls every
  // few seconds forever is a background cost with no reader.
  const transitioning = useMemo(
    () => Object.values(status).some((s) => s.phase === "starting" || s.phase === "stopping"),
    [status],
  );
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const d = await api.get("/api/ocrlab/status");
      if (!alive) return;
      setStatus(Object.fromEntries(d.models.map((m: Status) => [m.id, m])));
    };
    tick();
    const ms = transitioning ? 5000 : 30000;
    const t = setInterval(tick, ms);
    return () => { alive = false; clearInterval(t); };
  }, [transitioning]);

  const onFile = useCallback(async (file: File) => {
    setErr(""); setResults(null); setSaved(false);
    const buf = await file.arrayBuffer();
    setPdf(buf);
    const { page: p, count } = await renderPdfPage(buf, 1);
    setPage(p); setPageCount(count); setPageNum(1);
    // Upload in the background so a saved run can be reopened with its source.
    try {
      const { url, key } = await api.post("/api/ocrlab/uploads", {
        filename: file.name, contentType: file.type || "application/pdf",
      });
      await fetch(url, { method: "PUT", body: file, headers: { "Content-Type": file.type || "application/pdf" } });
      setUploadKey(key);
    } catch { /* the comparison still works; only re-opening the source is lost */ }
  }, []);

  const gotoPage = useCallback(async (n: number) => {
    if (!pdf || n < 1 || n > pageCount) return;
    const { page: p } = await renderPdfPage(pdf, n);
    setPage(p); setPageNum(n); setResults(null); setSaved(false);
  }, [pdf, pageCount]);

  const compare = useCallback(async () => {
    if (!page) return;
    setRunning(true); setErr(""); setResults(null); setSaved(false);
    try {
      // The page PNG goes to S3 first. It is far too large for the async invoke
      // that starts the worker (256KB cap), and this keeps it out of the API
      // payload entirely.
      const keys: string[] = [];
      const last = Math.min(pageNum + pageCountToRun - 1, pageCount || pageNum);
      for (let n = pageNum; n <= last; n++) {
        setErr(`Preparing page ${n} of ${last}…`);
        const rendered = n === pageNum ? page : (await renderPdfPage(pdf!, n)).page;
        const { url, key } = await api.post("/api/ocrlab/page-uploads", {});
        const bytes = Uint8Array.from(atob(rendered.base64), (c) => c.charCodeAt(0));
        await fetch(url, { method: "PUT", body: bytes, headers: { "Content-Type": "image/png" } });
        keys.push(key);
      }
      setErr("");

      const { jobId } = await api.post("/api/ocrlab/compare", {
        pageKeys: keys,
        page: { width: page.width, height: page.height },
        models: selected,
        prompts, uploadKey, pageNumber: pageNum, idleTimeoutMinutes: idleTimeout,
      });

      // Poll. A run is ~10s with both models warm and up to ~4 min if one is
      // cold, which is why this is a job rather than a request.
      const started = Date.now();
      for (;;) {
        await new Promise((r) => setTimeout(r, 2000));
        const job = await api.get(`/api/ocrlab/compare/${jobId}`);
        setElapsed(Math.round((Date.now() - started) / 1000));
        if (job.status === "done") {
          setResults(job.results);
          (window as any).__lastRun = { ...job, runId: job.jobId };
          break;
        }
        if (job.status === "failed") { setErr(job.error ?? "comparison failed"); break; }
        if (Date.now() - started > 15 * 60_000) { setErr("Gave up waiting after 15 minutes."); break; }
      }
    } catch (e: any) {
      setErr(e?.message ?? "comparison failed");
    } finally {
      setRunning(false); setElapsed(0);
    }
  }, [page, pdf, pageCount, selected, prompts, uploadKey, pageNum, idleTimeout, pageCountToRun]);

  const save = useCallback(async () => {
    const run = (window as any).__lastRun;
    if (!run) return;
    await api.post("/api/ocrlab/runs", { ...run, label: saveLabel.trim() || `Page ${run.pageNumber}` });
    setSaved(true);
  }, [saveLabel]);

  const anyOff = models.some((m) => status[m.id]?.phase !== "ready");

  return (
    <div className="mx-auto max-w-[1800px] px-6 py-6 space-y-6">
      <header>
        <p className="text-fh-body text-fh-text-secondary">
          One page, both models, side by side. GPUs run only while you need them.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-fh-meta">
        <div className="flex items-center gap-2">
          <span className="text-fh-text-secondary">Run:</span>
          {models.map((m) => (
            <label key={m.id} className="flex cursor-pointer items-center gap-1.5 text-fh-text-body">
              <input
                type="checkbox"
                checked={selected.includes(m.id)}
                onChange={(e) =>
                  setSelected((cur) =>
                    e.target.checked ? [...cur, m.id] : cur.filter((x) => x !== m.id),
                  )
                }
              />
              {m.label}
            </label>
          ))}
        </div>
        <label className="flex items-center gap-2 text-fh-text-secondary">
          Pages
          <select
            value={pageCountToRun}
            onChange={(e) => setPageCountToRun(Number(e.target.value))}
            className="h-8 rounded-fh-input border border-fh-border-input px-2 text-fh-body text-fh-text-body"
          >
            {[1, 2, 3, 5, 8, 12].map((n) => (
              <option key={n} value={n}>{n === 1 ? "this page" : `${n} from here`}</option>
            ))}
          </select>
        </label>
        {pageCountToRun > 1 && (
          <span className="text-fh-meta text-fh-text-muted">
            Unlimited-OCR takes all {pageCountToRun} in ONE pass; dots.ocr runs {pageCountToRun}{" "}
            separate requests — that difference is the point of this setting.
          </span>
        )}
        <label className="flex items-center gap-2 text-fh-text-secondary">
          Auto-stop after
          <select
            value={idleTimeout}
            onChange={(e) => setIdleTimeout(Number(e.target.value))}
            className="h-8 rounded-fh-input border border-fh-border-input px-2 text-fh-body text-fh-text-body"
          >
            {[10, 20, 30, 45, 60].map((n) => <option key={n} value={n}>{n} min idle</option>)}
          </select>
        </label>
      </div>

      <ModelBar
        models={models}
        status={status}
        onStart={(id) => api.post(`/api/ocrlab/models/${id}/start`, {}).then((s) => setStatus((p) => ({ ...p, [id]: s })))}
        onStop={(id) => api.post(`/api/ocrlab/models/${id}/stop`, {}).then((s) => setStatus((p) => ({ ...p, [id]: s })))}
      />

      <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr] gap-6">
        <div className="space-y-4">
          <PageCanvas
            page={page}
            overlayBlocks={overlay ? results?.find((r) => r.modelId === overlay)?.blocks ?? null : null}
            onFile={onFile}
            pageNum={pageNum}
            pageCount={pageCount}
            onGoto={gotoPage}
          />
          {results && (
            <div className="flex flex-wrap items-center gap-2 text-fh-meta">
              <span className="text-fh-text-secondary">Overlay boxes:</span>
              <button onClick={() => setOverlay(null)}
                className={`rounded px-2 py-1 ring-1 ${!overlay ? "bg-fh-navy text-white ring-fh-navy" : "ring-fh-border text-slate-600"}`}>
                None
              </button>
              {models.map((m) => (
                <button key={m.id} onClick={() => setOverlay(m.id)}
                  className={`rounded px-2 py-1 ring-1 ${overlay === m.id ? "bg-fh-navy text-white ring-fh-navy" : "ring-fh-border text-slate-600"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={compare} disabled={!page || running}>
              {running ? `Running… ${elapsed}s` : "Compare"}
            </Button>
            {results && (
              <>
                <input
                  value={saveLabel}
                  onChange={(e) => setSaveLabel(e.target.value)}
                  placeholder="Label this run…"
                  className="h-9 rounded-fh-input border border-fh-border-input px-2.5 text-fh-body"
                />
                <Button variant="secondary" onClick={save} disabled={saved}>
                  {saved ? "Saved ✓" : "Save run"}
                </Button>
              </>
            )}
            {running && (
              // A run against a cold model legitimately takes minutes. Saying so
              // is the difference between "working" and "frozen".
              <span className="text-fh-meta text-fh-text-muted">
                Comparing. A model that was Off has to start first (~4 min).
              </span>
            )}
            {anyOff && !running && (
              // Said plainly rather than hidden: a cold model turns Compare into
              // a multi-minute wait, and people assume a hang.
              <span className="text-fh-meta text-fh-priority-normal">
                A model that is Off will cold-start on first request (~4 min). Press Start to pre-warm.
              </span>
            )}
            {err && <span className="text-fh-meta text-fh-danger">{err}</span>}
          </div>

          {results && <ComparisonBar models={models} results={results} />}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {models.map((m) => (
              <ResultColumn
                key={m.id}
                model={m}
                prompt={prompts[m.id] ?? ""}
                onPrompt={(v) => setPrompts((p) => ({ ...p, [m.id]: v }))}
                result={results?.find((r) => r.modelId === m.id) ?? null}
                running={running}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
